import { CartItemsComponent } from '@theme/component-cart-items';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { CartSectionRestoredEvent, ThemeEvents } from '@theme/events';
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
import { DrawerOpenEvent, DrawerCloseEvent } from '@theme/theme-drawer';

// Keep a deliberate choice for options such as size; only single-value options
// are preselected. Changing an earlier option clears incompatible later choices.
export function resolveRelatedOptions(variants, selected, count) {
  const values = [];
  const choices = [];
  const available = variants.filter((variant) => variant.available);
  for (let index = 0; index < count; index += 1) {
    const all = [...new Set(variants.map((variant) => variant.options[index]))];
    const enabled = new Set(available.filter((variant) => values.every((value, position) => !value || variant.options[position] === value))
      .map((variant) => variant.options[index]));
    values[index] = enabled.has(selected[index]) ? selected[index] : all.length === 1 && enabled.has(all[0]) ? all[0] : '';
    choices[index] = all.map((value) => ({ value, enabled: enabled.has(value) }));
  }
  return {
    values,
    choices,
    variant: values.every(Boolean) ? available.find((variant) => variant.options.every((value, index) => value === values[index])) : undefined,
  };
}

function errorWithCart(code, cart) {
  const error = new Error(code);
  error.cart = cart;
  return error;
}

// A lost response must not cause a second add. Read the basket to confirm the
// result, including when the variant already exists, before reporting success.
export async function addRelatedVariant(request, variantId, sections) {
  const quantity = (cart) => cart.items.filter((item) => String(item.variant_id) === String(variantId)
    && !item.selling_plan_allocation && !item.parent_relationship && !Object.keys(item.properties ?? {}).length)
    .reduce((total, item) => total + item.quantity, 0);
  const before = await request('cart.js');
  const beforeQuantity = quantity(before);
  let added;
  try {
    added = await request('cart/add.js', { items: [{ id: Number(variantId), quantity: 1 }], ...sections });
  } catch (_) { /* The confirmation read below resolves an uncertain response. */ }
  let after;
  try { after = await request('cart.js'); } catch (_) { throw errorWithCart('review'); }
  if (quantity(after) === beforeQuantity + 1) return { ...after, sections: added?.sections };
  throw errorWithCart(quantity(after) === beforeQuantity ? 'unavailable' : 'review', after);
}

function node(tag, className, text) {
  const element = document.createElement(tag);
  if (className) element.className = className;
  if (text !== undefined) element.textContent = text;
  return element;
}

class CartRelatedProducts extends HTMLElement {
  static busy = false;
  static nextId = 0;

  connectedCallback() {
    this.listeners?.abort();
    this.listeners = new AbortController();
    const options = { signal: this.listeners.signal };
    this.labels = JSON.parse(this.querySelector('[data-related-labels]').textContent);
    if (this.dataset.context === 'catalogue') return;
    this.drawer = this.closest('theme-drawer');
    this.drawer?.addEventListener(DrawerOpenEvent.eventName, () => this.load(), options);
    this.drawer?.addEventListener(DrawerCloseEvent.eventName, () => this.closePanel(false), options);
    this.list = this.querySelector('[data-related-list]');
    this.list.addEventListener('scroll', () => this.updateArrows(), { ...options, passive: true });
    this.resizeObserver = new ResizeObserver(() => this.updateArrows());
    this.resizeObserver.observe(this.list);
    document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
      event.promise?.then(() => requestAnimationFrame(() => this.load(true))).catch(() => {});
    }, options);
    this.closest('cart-items-component')?.addEventListener(ThemeEvents.cartSectionRestored, () => this.load(true), options);
    this.addEventListener('click', (event) => this.onClick(event), options);
    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.panel && !CartRelatedProducts.busy) {
        event.preventDefault();
        event.stopPropagation();
        this.closePanel();
      }
    }, options);
    if (this.dataset.context === 'page' || this.drawer?.hasAttribute('open')) this.load();
  }

  disconnectedCallback() {
    this.closePanel(false);
    this.resizeObserver?.disconnect();
    this.listeners?.abort();
    this.recommendationsFetch?.abort();
    this.productFetch?.abort();
  }

  async load(force = false) {
    if (!this.isConnected || (this.dataset.context !== 'page' && !this.drawer?.hasAttribute('open')) || CartRelatedProducts.busy) return;
    const ids = this.dataset.productIds.split(',').filter(Boolean);
    if (!force && !this.hidden && this.querySelector('[data-related-card]')) return;
    this.recommendationsFetch?.abort();
    const controller = new AbortController();
    this.recommendationsFetch = controller;
    this.closePanel(false);
    const list = this.querySelector('[data-related-list]');
    list.replaceChildren();
    this.hidden = true;
    if (!ids.length) return;
    try {
      let products = [];
      for (const id of ids.slice(0, 2)) {
        const url = new URL(this.dataset.recommendationsUrl + '.json', window.location.origin);
        url.search = new URLSearchParams({ product_id: id, limit: '8', intent: 'related' });
        const response = await fetch(url, { signal: controller.signal, credentials: 'same-origin', headers: { Accept: 'application/json' } });
        if (!response.ok) continue;
        const result = await response.json();
        products = (result.products ?? []).filter((product) => product.available && !ids.includes(String(product.id)) && !product.requires_selling_plan).slice(0, 6);
        if (products.length) break;
      }
      if (controller.signal.aborted || !this.isConnected) return;
      this.products = products;
      list.replaceChildren(...products.map((product, index) => this.makeCard(product, index)));
      this.hidden = !products.length;
      requestAnimationFrame(() => this.updateArrows());
    } catch (_) { /* An empty recommendation slot must never interrupt checkout. */ }
  }

  money(price) {
    return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
      style: 'currency', currency: window.Shopify?.currency?.active ?? this.dataset.currency,
    }).format(price / 100);
  }

  productUrl(product) {
    const url = new URL(product.url || `${window.Shopify?.routes?.root ?? '/'}products/${product.handle}`, window.location.origin);
    if (url.origin !== window.location.origin) throw new Error('loadFailed');
    return url;
  }

  updateArrows() {
    const list = this.list;
    if (!list) return;
    const max = list.scrollWidth - list.clientWidth;
    this.querySelector('[data-related-arrows]').hidden = max < 2;
    const position = Math.abs(list.scrollLeft);
    this.querySelector('[data-related-scroll="-1"]').disabled = position < 2;
    this.querySelector('[data-related-scroll="1"]').disabled = position >= max - 2;
  }

  scrollCards(direction) {
    const card = this.list.querySelector('[data-related-card]');
    const distance = card ? card.getBoundingClientRect().width + 12 : this.list.clientWidth;
    const rtl = getComputedStyle(this.list).direction === 'rtl' ? -1 : 1;
    this.list.scrollBy({ left: direction * rtl * distance, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
  }

  imageUrl(source, width) {
    if (typeof source !== 'string' || !source) return '';
    const url = new URL(source, window.location.origin);
    if (!['https:', 'http:'].includes(url.protocol)) return '';
    url.searchParams.set('width', String(width));
    return url.href;
  }

  availablePrice(product) {
    const prices = (product.variants ?? []).filter(variant => variant.available).map(variant => Number(variant.price)).filter(Number.isFinite);
    const minimum = prices.length ? Math.min(...prices) : product.price;
    const varies = prices.length ? Math.max(...prices) !== minimum : product.price_varies;
    return (varies ? this.labels.from + ' ' : '') + this.money(minimum);
  }

  makeCard(product, index) {
    const card = node('article', 'about-cart-related__card');
    card.dataset.relatedCard = String(index);
    const imageLink = node('a', 'about-cart-related__image-link');
    imageLink.href = this.productUrl(product).href;
    imageLink.tabIndex = -1;
    imageLink.setAttribute('aria-hidden', 'true');
    const source = this.imageUrl(product.featured_image?.url || product.featured_image, 400);
    if (source) {
      const image = node('img', 'about-cart-related__image');
      image.src = source;
      image.alt = '';
      image.width = 200;
      image.height = 200;
      image.loading = 'lazy';
      imageLink.append(image);
    }
    const chars = Array.from(product.title);
    const title = node('a', 'about-cart-related__title', chars.length > 64 ? chars.slice(0, 61).join('').trimEnd() + '...' : product.title);
    title.href = this.productUrl(product).href;
    title.title = product.title;
    title.setAttribute('aria-label', product.title);
    const price = node('p', 'about-cart-related__price', this.availablePrice(product));
    const button = node('button', 'about-cart-related__open', this.labels.quickAdd);
    button.type = 'button';
    button.dataset.relatedOpen = String(index);
    button.setAttribute('aria-expanded', 'false');
    button.setAttribute('aria-haspopup', 'dialog');
    button.setAttribute('aria-label', `${this.labels.quickAdd}: ${product.title}`);
    card.append(imageLink, title, price, button);
    return card;
  }

  onClick(event) {
    const target = event.target.closest('button');
    if (!target || CartRelatedProducts.busy || target.disabled) return;
    if (target.hasAttribute('data-related-open')) this.openPanel(target);
    else if (target.hasAttribute('data-related-close')) this.closePanel();
    else if (target.hasAttribute('data-related-scroll')) this.scrollCards(Number(target.dataset.relatedScroll));
    else if (target.hasAttribute('data-related-image')) this.showImage(Number(target.dataset.relatedImage));
    else if (target.hasAttribute('data-related-image-step')) this.showImage(this.imageIndex + Number(target.dataset.relatedImageStep));
    else if (target.hasAttribute('data-related-choice')) {
      this.values[Number(target.dataset.optionIndex)] = target.dataset.relatedChoice;
      this.updateOptions();
    } else if (target.hasAttribute('data-related-add')) this.add();
  }

  closePanel(focus = true) {
    this.productFetch?.abort();
    this.overlayListeners?.abort();
    this.panel?.remove();
    this.panel = null;
    this.pageDialog?.close();
    this.pageDialog?.remove();
    this.pageDialog = null;
    for (const state of this.backgroundStates ?? []) state.element.inert = state.inert;
    this.backgroundStates = [];
    if (this.opener) {
      this.opener.setAttribute('aria-expanded', 'false');
      this.opener.removeAttribute('aria-controls');
      if (focus && this.opener.isConnected) this.opener.focus({ preventScroll: true });
    }
    this.opener = null;
  }

  overlayKey(event) {
    if (event.isComposing) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (!CartRelatedProducts.busy) this.closePanel();
      return;
    }
    if (event.key !== 'Tab') return;
    event.stopImmediatePropagation();
    const focusable = [...this.panel.querySelectorAll('button:not(:disabled), a[href], summary, [tabindex="0"]')].filter(element => element.getClientRects().length && !element.closest('[inert]'));
    const first = focusable[0];
    const last = focusable.at(-1);
    if (!focusable.length) { event.preventDefault(); this.panel.focus(); }
    else if (event.shiftKey && (document.activeElement === first || !this.panel.contains(document.activeElement))) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && (document.activeElement === last || !this.panel.contains(document.activeElement))) { event.preventDefault(); first.focus(); }
  }

  openCatalogueProduct(button, product) {
    if (CartRelatedProducts.busy || this.dataset.context !== 'catalogue') return;
    this.products = [product];
    button.dataset.relatedOpen = '0';
    return this.openPanel(button);
  }

  async openPanel(button) {
    this.closePanel(false);
    const recommended = this.products[Number(button.dataset.relatedOpen)];
    if (!recommended) return;
    const isCatalogue = this.dataset.context === 'catalogue';
    const isPage = this.dataset.context === 'page' || isCatalogue;
    let host = this.drawer?.querySelector('dialog');
    if (isPage) {
      host = node('dialog', 'about-cart-product-modal' + (isCatalogue ? ' about-cart-product-modal--catalogue' : ''));
      this.pageDialog = host;
      this.parentElement.append(host);
    }
    if (!host) return;
    const controller = new AbortController();
    this.productFetch = controller;
    this.overlayListeners = new AbortController();
    const options = { signal: this.overlayListeners.signal };
    this.opener = button;
    const panel = node('div', 'about-cart-product');
    this.panel = panel;
    panel.id = `about-related-options-${++CartRelatedProducts.nextId}`;
    panel.tabIndex = -1;
    if (isPage) {
      host.setAttribute('aria-labelledby', panel.id + '-title');
    } else {
      panel.setAttribute('role', 'dialog');
      panel.setAttribute('aria-modal', 'true');
      panel.setAttribute('aria-labelledby', panel.id + '-title');
    }
    const header = node('div', 'about-cart-product__header');
    const back = node('button', 'about-cart-product__back');
    back.type = 'button';
    back.dataset.relatedClose = '';
    const arrow = node('span', '', '‹');
    arrow.setAttribute('aria-hidden', 'true');
    back.append(arrow, document.createTextNode(isCatalogue ? this.labels.close : this.labels.back));
    header.append(back);
    this.body = node('div', 'about-cart-product__body');
    this.gallery = node('div', 'about-cart-product__gallery');
    this.gallery.setAttribute('aria-label', this.labels.gallery);
    this.gallery.hidden = true;
    const heading = node('h2', 'about-cart-product__title', recommended.title);
    heading.id = panel.id + '-title';
    this.options = node('div', 'about-cart-product__options');
    this.price = node('p', 'about-cart-product__price', this.availablePrice(recommended));
    this.description = node('div', 'about-cart-product__description');
    this.status = node('p', 'about-cart-related__status', this.labels.loading);
    this.status.setAttribute('role', 'status');
    this.status.setAttribute('aria-live', 'polite');
    this.addButton = node('button', 'about-cart-related__add', this.labels.add);
    this.addButton.type = 'button';
    this.addButton.dataset.relatedAdd = '';
    this.addButton.disabled = true;
    this.details = node('a', 'about-cart-related__details', this.labels.details);
    this.details.href = this.productUrl(recommended).href;
    const footer = node('div', 'about-cart-product__footer');
    footer.append(this.status, this.addButton);
    const policies = node('div', 'about-cart-product__policies');
    for (const [label, url] of [[this.labels.delivery, this.labels.shippingUrl], [this.labels.returns, this.labels.returnsUrl]]) {
      if (!url) continue;
      const link = node('a', '', label);
      link.href = url;
      policies.append(link);
    }
    this.body.append(this.gallery, heading, this.price, this.options, this.description, this.details, policies);
    panel.append(header, this.body, footer);
    this.backgroundStates = isPage ? [] : [...host.children].map(element => ({ element, inert: element.inert }));
    for (const state of this.backgroundStates) state.element.inert = true;
    host.append(panel);
    panel.addEventListener('click', event => this.onClick(event), options);
    host.addEventListener('keydown', event => this.overlayKey(event), { ...options, capture: true });
    host.addEventListener('cancel', event => { event.preventDefault(); if (!CartRelatedProducts.busy) this.closePanel(); }, options);
    if (isPage) host.showModal();
    button.setAttribute('aria-expanded', 'true');
    button.setAttribute('aria-controls', panel.id);
    back.focus({ preventScroll: true });
    try {
      const url = this.productUrl(recommended);
      url.pathname = url.pathname.replace(/\/$/, '') + '.js';
      url.search = '';
      const response = await fetch(url, { signal: controller.signal, credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('loadFailed');
      const product = await response.json();
      if (controller.signal.aborted || panel !== this.panel) return;
      if (!product.variants?.length || product.variants.length >= 250 || product.requires_selling_plan) throw new Error('loadFailed');
      if (!product.variants.some(variant => variant.available)) throw new Error('unavailable');
      this.product = product;
      heading.textContent = product.title;
      this.values = [];
      this.lastVariantImage = null;
      this.buildGallery(product);
      this.renderDescription(product.description);
      this.fields = product.options.map((option, index) => {
        const name = typeof option === 'string' ? option : option.name;
        const label = /^colou?r$/i.test(name) ? this.labels.colour : name;
        const field = node('fieldset', 'about-cart-related__field');
        field.dataset.optionType = /^size$/i.test(name) ? 'size' : 'standard';
        const legend = node('legend', '', label);
        const choices = node('div', 'about-cart-related__choices');
        const values = [...new Set(product.variants.map(variant => variant.options[index]))];
        for (const value of values) {
          const choice = node('button', 'about-cart-related__choice', value);
          choice.type = 'button';
          choice.dataset.relatedChoice = value;
          choice.dataset.optionIndex = String(index);
          choices.append(choice);
        }
        field.append(legend, choices);
        field.hidden = name === 'Title' && values.length === 1 && values[0] === 'Default Title';
        this.options.append(field);
        return { field, legend, label };
      });
      this.updateOptions();
    } catch (error) {
      if (!controller.signal.aborted && panel === this.panel) this.status.textContent = this.labels[error.message] ?? this.labels.loadFailed;
    }
  }



  buildGallery(product) {
    this.images = (product.images ?? []).map(image => ({ src: typeof image === 'string' ? image : image.src || image.url, alt: image.alt || product.title })).filter(image => this.imageUrl(image.src, 900));
    if (!this.images.length && product.featured_image) this.images = [{ src: product.featured_image?.src || product.featured_image, alt: product.title }];
    this.gallery.hidden = !this.images.length;
    if (!this.images.length) return;
    const stage = node('div', 'about-cart-product__stage');
    this.heroImage = node('img', 'about-cart-product__image');
    this.heroImage.width = 640;
    this.heroImage.height = 640;
    this.heroImage.decoding = 'async';
    const previous = node('button', 'about-cart-product__image-arrow', '‹');
    const next = node('button', 'about-cart-product__image-arrow', '›');
    [previous, next].forEach((button, index) => {
      button.type = 'button';
      button.dataset.relatedImageStep = index ? '1' : '-1';
      button.setAttribute('aria-label', index ? this.labels.nextImage : this.labels.previousImage);
      button.hidden = this.images.length < 2;
    });
    this.imageCount = node('span', 'about-cart-product__image-count');
    stage.append(this.heroImage, previous, next, this.imageCount);
    this.thumbnails = node('div', 'about-cart-product__thumbnails');
    this.thumbnails.hidden = this.images.length < 2;
    this.images.forEach((image, index) => {
      const button = node('button', 'about-cart-product__thumbnail');
      button.type = 'button';
      button.dataset.relatedImage = String(index);
      button.setAttribute('aria-label', this.labels.image.replace('[number]', String(index + 1)).replace('[total]', String(this.images.length)));
      const thumb = node('img');
      thumb.src = this.imageUrl(image.src, 120);
      thumb.alt = '';
      thumb.width = 56;
      thumb.height = 56;
      thumb.loading = 'lazy';
      button.append(thumb);
      this.thumbnails.append(button);
    });
    this.gallery.replaceChildren(stage, this.thumbnails);
    this.showImage(0);
  }

  showImage(index) {
    if (!this.images?.length) return;
    this.imageIndex = (index + this.images.length) % this.images.length;
    const image = this.images[this.imageIndex];
    this.heroImage.src = this.imageUrl(image.src, 900);
    this.heroImage.alt = image.alt;
    this.imageCount.textContent = `${this.imageIndex + 1} / ${this.images.length}`;
    for (const button of this.thumbnails.children) button.setAttribute('aria-pressed', String(Number(button.dataset.relatedImage) === this.imageIndex));
    const selected = this.thumbnails.children[this.imageIndex];
    if (selected) this.thumbnails.scrollTo({ left: selected.offsetLeft - this.thumbnails.offsetLeft - this.thumbnails.clientWidth / 2 + selected.clientWidth / 2, behavior: 'instant' });
  }

  renderDescription(html) {
    this.description.replaceChildren();
    if (!html) return;
    const heading = node('h3', '', this.labels.description);
    const content = node('div', 'about-cart-product__copy');
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const allowed = new Set(['P', 'BR', 'STRONG', 'B', 'EM', 'I', 'U', 'UL', 'OL', 'LI', 'H2', 'H3', 'H4', 'DIV', 'SPAN', 'TABLE', 'THEAD', 'TBODY', 'TR', 'TD', 'TH', 'A', 'IMG', 'HR', 'BLOCKQUOTE', 'DETAILS', 'SUMMARY']);
    for (const element of [...parsed.body.querySelectorAll('*')]) {
      if (!allowed.has(element.tagName)) { element.remove(); continue; }
      for (const attribute of [...element.attributes]) {
        if (!['href', 'src', 'alt', 'colspan', 'rowspan'].includes(attribute.name)) element.removeAttribute(attribute.name);
      }
      for (const attribute of ['href', 'src']) {
        const raw = element.getAttribute(attribute);
        if (!raw) continue;
        try { const url = new URL(raw, window.location.origin); if (!['http:', 'https:'].includes(url.protocol)) element.removeAttribute(attribute); }
        catch (_) { element.removeAttribute(attribute); }
      }
      if (element.tagName === 'IMG') { element.loading = 'lazy'; element.alt ||= this.product.title; }
    }
    content.append(...parsed.body.childNodes);
    this.description.append(heading, content);
  }

  updateOptions() {
    const resolved = resolveRelatedOptions(this.product.variants, this.values, this.fields.length);
    this.values = resolved.values;
    this.fields.forEach(({ field, legend, label }, index) => {
      legend.textContent = label;
      if (this.values[index]) {
        legend.append(document.createTextNode(': '), node('span', 'about-cart-related__selected-value', this.values[index]));
      }
      for (const button of field.querySelectorAll('button')) {
        const choice = resolved.choices[index].find((item) => item.value === button.dataset.relatedChoice);
        button.disabled = !choice.enabled;
        button.setAttribute('aria-pressed', String(choice.value === this.values[index]));
      }
    });
    this.selectedVariant = resolved.variant;
    const rule = resolved.variant?.quantity_rule;
    const needsProductPage = rule && (rule.min > 1 || rule.increment > 1);
    this.addButton.disabled = !resolved.variant || Boolean(needsProductPage);
    this.price.textContent = resolved.variant ? this.money(resolved.variant.price) : this.availablePrice(this.product);
    const preview = resolved.variant ?? this.product.variants.find(variant => variant.available && this.values.every((value, index) => !value || variant.options[index] === value));
    const featured = preview?.featured_image?.src || preview?.featured_image?.url;
    if (featured && featured !== this.lastVariantImage) {
      const index = this.images.findIndex(image => new URL(image.src, window.location.origin).pathname === new URL(featured, window.location.origin).pathname);
      if (index >= 0) this.showImage(index);
      this.lastVariantImage = featured;
    }
    this.status.textContent = needsProductPage ? this.labels.loadFailed : resolved.variant ? '' : this.labels.select;
    if (resolved.variant) {
      const url = this.productUrl(this.product);
      url.searchParams.set('variant', String(resolved.variant.id));
      this.details.href = url.href;
    }
  }

  async request(route, body) {
    const response = await fetch((window.Shopify?.routes?.root ?? '/') + route, {
      method: body ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store',
      headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
    const data = await response.json();
    if (!response.ok || data.status >= 400 || data.errors) throw new Error('failed');
    return data;
  }

  async add() {
    if (CartRelatedProducts.busy || !this.selectedVariant || this.addButton.disabled) return;
    const isCatalogue = this.dataset.context === 'catalogue';
    const owner = isCatalogue ? document.querySelector('#cart-drawer cart-items-component') : this.closest('cart-items-component');
    if (!owner?.dataset.sectionId) {
      this.status.textContent = this.labels.loadFailed;
      return;
    }
    const sectionId = owner.dataset.sectionId;
    const mode = owner.dataset.drawer !== undefined ? 'hydration' : 'full';
    const variantId = this.selectedVariant.id;
    const productTitle = this.product.title;
    CartRelatedProducts.busy = true;
    this.addButton.disabled = true;
    this.addButton.textContent = this.labels.adding;
    this.panel?.setAttribute('aria-busy', 'true');
    this.options.inert = true;
    const locked = [...document.querySelectorAll('cart-items-component, product-form-component')].map((element) => ({ element, inert: element.inert }));
    for (const { element } of locked) element.inert = true;
    this.status.textContent = this.labels.adding;
    this.setAttribute('aria-busy', 'true');
    let deferred;
    let cart;
    let message;
    let failed = false;
    try {
      await new Promise((resolve) => setTimeout(resolve, 350));
      await Promise.allSettled(CartItemsComponent.pendingQuantityUpdates);
      const ids = [...new Set([...document.querySelectorAll('cart-items-component')].map((element) => element.dataset.sectionId).filter(Boolean))].slice(0, 5);
      ids.forEach((id) => sectionRenderer.abortRender(id));
      deferred = CartLinesUpdateEvent.createPromise();
      const currentOwner = document.querySelector(`cart-items-component[data-section-id="${sectionId}"]`);
      (currentOwner ?? owner).dispatchEvent(new CartLinesUpdateEvent({ action: 'add', context: 'cart', lines: [], promise: deferred.promise }));
      cart = await addRelatedVariant(this.request.bind(this), variantId, { sections: ids, sections_url: window.location.pathname });
      message = this.labels.added.replace('[product]', productTitle);
    } catch (error) {
      failed = true;
      message = this.labels[error.message] ?? this.labels.failed;
      cart = error.cart;
      if (!cart) { try { cart = await this.request('cart.js'); } catch (_) { message = this.labels.review; } }
    }
    this.closePanel(false);
    try {
      if (cart) {
        if (cart.sections?.[sectionId]) await morphSection(sectionId, cart.sections[sectionId], { mode });
        else await sectionRenderer.renderSection(sectionId, { cache: false, mode });
        deferred?.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: {
          sections: cart.sections, items: cart.items, itemCount: cart.item_count, source: 'cart-related-products', didError: failed,
        } });
        document.querySelector(`cart-items-component[data-section-id="${sectionId}"]`)?.dispatchEvent(new CartSectionRestoredEvent());
        for (const selector of document.querySelectorAll('quantity-selector-component[data-variant-id]')) {
          const quantity = cart.items.filter((item) => String(item.variant_id) === selector.dataset.variantId).reduce((total, item) => total + item.quantity, 0);
          selector.querySelector('input[data-cart-quantity]')?.setAttribute('data-cart-quantity', String(quantity));
          selector.updateCartQuantity?.();
        }
        for (const selector of document.querySelectorAll('cart-quantity-selector-component')) selector.updateButtonStates?.();
      } else deferred?.reject(new Error(message));
    } catch (_) {
      failed = true;
      message = this.labels.refresh;
      deferred?.resolve({ cart: cart ? CartLinesUpdateEvent.createCartFromAjaxResponse(cart) : undefined, detail: { itemCount: cart?.item_count, didError: true } });
    } finally {
      for (const { element, inert } of locked) element.inert = inert;
      this.removeAttribute('aria-busy');
      CartRelatedProducts.busy = false;
      const current = document.querySelector(`cart-items-component[data-section-id="${sectionId}"]`);
      let feedback = current?.querySelector('.about-cart-editor-feedback');
      if (current && !feedback) {
        feedback = node('p', 'about-cart-editor-feedback');
        feedback.tabIndex = -1;
        (current.querySelector('.cart-items__wrapper') ?? current).prepend(feedback);
      }
      if (isCatalogue) document.getElementById('cart-drawer')?.open();
      if (feedback) {
        feedback.setAttribute('role', failed ? 'alert' : 'status');
        feedback.textContent = message;
        if (isCatalogue) requestAnimationFrame(() => feedback.focus({ preventScroll: true }));
        else feedback.focus();
      }
      current?.querySelector('about-cart-related')?.load(true);
    }
  }
}

if (!customElements.get('about-cart-related')) customElements.define('about-cart-related', CartRelatedProducts);

